// ============================================================================
// Puente a ingeniería (#510) — estado dentro del panel del Agente IA.
// ============================================================================
// Al aprobar, el backend encola el trabajo del runner y crea (o reutiliza) el
// issue de GitHub. Aquí se ve en qué quedó y, si quedó pendiente o falló, se
// reintenta. El texto del motivo lo escribe el backend (p. ej. «Falta
// GH_TOKEN…»), así que se muestra tal cual.
// ============================================================================
import { useTranslation } from "react-i18next";
import {
  bridgeStateOf,
  canRetryEngineeringBridge,
  ENGINEERING_BRIDGE_COLORS,
  type EngineeringBridgeInfo,
} from "../services/engineeringBridge";

interface Props {
  bridge?: EngineeringBridgeInfo | null;
  isDarkMode: boolean;
  busy?: boolean;
  onRetry: () => void;
}

export function EngineeringBridgeStatus({ bridge, isDarkMode, busy = false, onRetry }: Props) {
  const { t } = useTranslation("serviceDesk");
  if (!bridge) return null;
  const state = bridgeStateOf(bridge);
  const colors = ENGINEERING_BRIDGE_COLORS[state];
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";

  return (
    <div
      className={`mb-3 rounded-lg border p-3 text-xs ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}
      data-testid="engineering-bridge"
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`font-medium ${text}`}>{t("ticketDetail.bridge.title")}</span>
        <span className="rounded px-2 py-0.5 font-semibold" style={{ color: colors.color, backgroundColor: colors.bg }}>
          {t(`ticketDetail.bridge.states.${state}`)}
        </span>
      </div>
      {bridge.repo && <p className={`mt-1 font-mono ${muted}`}>{t("ticketDetail.bridge.repo", { repo: bridge.repo })}</p>}
      {bridge.message && <p className={`mt-1 ${muted}`}>{bridge.message}</p>}
      {state === "linked" && bridge.labelled === false && !bridge.message && (
        <p className={`mt-1 ${muted}`}>{t("ticketDetail.bridge.notLabelled")}</p>
      )}
      {canRetryEngineeringBridge(bridge) && (
        <button type="button" onClick={onRetry} disabled={busy} className="mt-2 font-semibold underline disabled:opacity-60">
          {t("ticketDetail.bridge.retry")}
        </button>
      )}
    </div>
  );
}
